/**
 * Structure review with inline asset editing.
 *
 * Two product rules are enforced here rather than described:
 *  1. Every LLM-generated asset is labelled as generated and shows its source evidence
 *     (`AdAsset.generated_by` / `AdAsset.evidence`, 00-product-brief.md flow 4).
 *  2. Nothing truncates an ad asset. Over-limit text is refused with the counted width
 *     (the same counter and the same limits the server enforces), and the asset is never
 *     silently shortened.
 *
 * Saving calls `PATCH /blueprints/{id}` — which never mutates: it creates version n+1,
 * and the review screen immediately diffs it against the version it came from.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useBlueprint, useEditBlueprint } from '@/api/hooks'
import type { AdAsset, AdGroupSpec, Blueprint, ResponsiveSearchAdSpec } from '@/api/types'
import {
  countCharacters,
  descriptionError,
  duplicateAssets,
  headlineError,
  pathError,
  pinningAdvisories,
  RSA_DESCRIPTION_MAX_CHARS,
  RSA_HEADLINE_MAX_CHARS,
  RSA_PATH_MAX_CHARS,
} from '@/lib/limits'
import { formatCompactNumber, formatMoneyMicros, formatNumber } from '@/lib/format'
import { Badge, Button, Cap, EmptyState, ErrorNotice, Input, Panel, Spinner } from '@/components/ui'

/** "AI" label with the model id, or an explicit "human" label. Never unlabelled. */
function GeneratedBadge({ asset }: { asset: AdAsset }) {
  if (asset.generated_by) {
    return (
      <Badge tone="ai" testId="asset-generated-badge" title={`Generated by ${asset.generated_by}`}>
        AI · {asset.generated_by}
      </Badge>
    )
  }
  return (
    <Badge tone="neutral" testId="asset-human-badge">
      human
    </Badge>
  )
}

function Evidence({ asset }: { asset: AdAsset }) {
  if (!asset.evidence) {
    return <p className="text-[11.5px] text-mist-500">no evidence recorded for this asset</p>
  }
  return (
    <p className="text-[11.5px] text-mist-500" data-testid="asset-evidence">
      <span className="label-cap mr-1.5">evidence</span>
      {asset.evidence}
    </p>
  )
}

function AssetRow({
  asset,
  index,
  kind,
  onChange,
  highlight,
}: {
  asset: AdAsset
  index: number
  kind: 'headline' | 'description'
  onChange: (next: AdAsset, index: number) => void
  highlight: boolean
}) {
  const limit = kind === 'headline' ? RSA_HEADLINE_MAX_CHARS : RSA_DESCRIPTION_MAX_CHARS
  const error = kind === 'headline' ? headlineError(asset.text) : descriptionError(asset.text)
  const counted = countCharacters(asset.text)
  const [editing, setEditing] = useState(false)

  return (
    <li
      data-testid={`asset-${kind}`}
      data-invalid={error ? 'true' : 'false'}
      className={`rounded-md border px-2.5 py-2 ${
        highlight ? 'border-forge-400 bg-forge-500/10' : error ? 'border-signal-danger/50 bg-signal-danger/5' : 'border-ink-750 bg-ink-850/60'
      }`}
    >
      <div className="flex items-start gap-2">
        <span className="num mt-[3px] w-5 shrink-0 text-[11px] text-mist-500">{index + 1}</span>
        <div className="min-w-0 flex-1">
          {editing ? (
            <Input
              autoFocus
              aria-label={`${kind} ${index + 1} text`}
              data-testid={`asset-${kind}-input-${index}`}
              value={asset.text}
              onChange={(event) => onChange({ ...asset, text: event.target.value }, index)}
              onBlur={() => setEditing(false)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === 'Escape') setEditing(false)
              }}
            />
          ) : (
            <button
              type="button"
              data-testid={`asset-${kind}-text-${index}`}
              onClick={() => setEditing(true)}
              className="w-full rounded text-left text-[13px] text-mist-100 hover:bg-ink-800"
              title="Click to edit"
            >
              {asset.text}
            </button>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <GeneratedBadge asset={asset} />
            {asset.pinned_position !== null ? (
              <Badge tone="info">pinned to {asset.pinned_position}</Badge>
            ) : null}
            {error ? (
              <span
                role="alert"
                className="text-[11.5px] text-signal-danger"
                data-testid={`asset-${kind}-error-${index}`}
              >
                {error}
              </span>
            ) : null}
          </div>
          <div className="mt-1">
            <Evidence asset={asset} />
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span
            className={`num text-[11.5px] ${error ? 'text-signal-danger' : counted > limit * 0.9 ? 'text-signal-warn' : 'text-mist-500'}`}
            title={`${counted} counted characters, limit ${limit}`}
          >
            {counted}/{limit}
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              aria-label={asset.pinned_position === null ? `Pin ${kind} ${index + 1}` : `Unpin ${kind} ${index + 1}`}
              onClick={() =>
                onChange(
                  {
                    ...asset,
                    pinned_position: asset.pinned_position === null ? Math.min(index + 1, kind === 'headline' ? 15 : 4) : null,
                  },
                  index,
                )
              }
            >
              {asset.pinned_position === null ? 'pin' : 'unpin'}
            </Button>
          </div>
        </div>
      </div>
    </li>
  )
}

function RsaEditor({
  ad,
  highlightPath,
  onChange,
}: {
  ad: ResponsiveSearchAdSpec
  highlightPath: string | null
  onChange: (next: ResponsiveSearchAdSpec) => void
}) {
  const dupes = duplicateAssets(ad.headlines.map((h) => h.text))
  const advisories = pinningAdvisories({
    headlines: ad.headlines.length,
    pinnedHeadlines: ad.headlines.filter((h) => h.pinned_position !== null).length,
    path1: ad.path1 ?? null,
  })

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 lg:grid-cols-2">
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <Cap>Headlines · {ad.headlines.length} (max 15)</Cap>
            <span className="text-[11px] text-mist-500">hard limit 30 counted characters</span>
          </div>
          <ul className="flex flex-col gap-1.5">
            {ad.headlines.map((asset, index) => (
              <AssetRow
                key={`h-${index}`}
                asset={asset}
                index={index}
                kind="headline"
                highlight={highlightPath === `headlines[${index}].text`}
                onChange={(next, position) => {
                  const headlines = [...ad.headlines]
                  headlines[position] = next
                  onChange({ ...ad, headlines })
                }}
              />
            ))}
          </ul>
          {dupes.length > 0 ? (
            <p role="alert" className="mt-1.5 text-[11.5px] text-signal-danger">
              duplicate headline text at positions {dupes.map((d) => d + 1).join(', ')} — both platforms
              reject identical assets in one RSA
            </p>
          ) : null}
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <Cap>Descriptions · {ad.descriptions.length} (max 4)</Cap>
            <span className="text-[11px] text-mist-500">hard limit 90 counted characters</span>
          </div>
          <ul className="flex flex-col gap-1.5">
            {ad.descriptions.map((asset, index) => (
              <AssetRow
                key={`d-${index}`}
                asset={asset}
                index={index}
                kind="description"
                highlight={highlightPath === `descriptions[${index}].text`}
                onChange={(next, position) => {
                  const descriptions = [...ad.descriptions]
                  descriptions[position] = next
                  onChange({ ...ad, descriptions })
                }}
              />
            ))}
          </ul>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {(['path1', 'path2'] as const).map((key) => {
              const value = ad[key] ?? null
              const error = value === null || value === '' ? null : pathError(value)
              return (
                <div key={key} className="flex flex-col gap-1">
                  <label htmlFor={`${key}-input`} className="label-cap">
                    {key} (≤{RSA_PATH_MAX_CHARS})
                  </label>
                  <Input
                    id={`${key}-input`}
                    data-testid={`rsa-${key}`}
                    value={value ?? ''}
                    onChange={(event) => onChange({ ...ad, [key]: event.target.value === '' ? null : event.target.value })}
                  />
                  {error ? (
                    <p role="alert" className="text-[11.5px] text-signal-danger">
                      {error}
                    </p>
                  ) : null}
                </div>
              )
            })}
            <div className="sm:col-span-2">
              <Cap>Final URL</Cap>
              <p className="num mt-0.5 truncate text-[12px] text-mist-300" title={ad.final_url}>
                {ad.final_url}
              </p>
              <p className="mt-0.5 text-[11px] text-mist-500">
                Landing pages publish before the campaign pushes: a campaign pointing at a 404 gets
                disapproved.
              </p>
            </div>
          </div>

          {advisories.length > 0 ? (
            <ul className="mt-3 flex flex-col gap-1" data-testid="advisories">
              {advisories.map((note) => (
                <li key={note} className="flex gap-2 text-[11.5px] text-signal-warn">
                  <span aria-hidden="true">▲</span>
                  {note}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function AdGroupBlock({
  campaignIndex,
  adGroup,
  adGroupIndex,
  highlightPath,
  onChange,
}: {
  campaignIndex: number
  adGroup: AdGroupSpec
  adGroupIndex: number
  highlightPath: string | null
  onChange: (next: AdGroupSpec) => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const prefix = `campaigns[${campaignIndex}].ad_groups[${adGroupIndex}]`
  const highlighted = highlightPath?.startsWith(prefix) ?? false

  useEffect(() => {
    if (highlighted) setOpen(true)
  }, [highlighted])

  const volumes = adGroup.keywords
    .map((keyword) => keyword.avg_monthly_searches ?? 0)
    .filter((value) => value > 0)

  return (
    <div
      ref={ref}
      data-testid="adgroup-block"
      data-path={prefix}
      className={`rounded-lg border ${highlighted ? 'border-forge-400' : 'border-ink-750'}`}
    >
      <button
        type="button"
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-ink-850/70"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span aria-hidden="true" className="text-[11px] text-mist-500">
          {open ? '▾' : '▸'}
        </span>
        <span className="text-[13px] font-medium text-mist-100">{adGroup.name}</span>
        <span className="text-[11.5px] text-mist-500">theme: {adGroup.theme}</span>
        <span className="num ml-auto text-[11.5px] text-mist-400">
          {adGroup.keywords.length} kw · {(adGroup.negatives ?? []).length} neg · {adGroup.ads.length} ad
        </span>
        {volumes.length > 0 ? (
          <span className="num text-[11.5px] text-mist-500">
            ≤{formatCompactNumber(Math.max(...volumes))} searches/mo
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="flex flex-col gap-4 border-t border-ink-800 px-3 py-3.5">
          {adGroup.ads.map((ad, adIndex) => (
            <RsaEditor
              key={`ad-${adIndex}`}
              ad={ad}
              highlightPath={highlightPath ? highlightPath.replace(`${prefix}.ads[${adIndex}].`, '') : null}
              onChange={(next) => {
                const ads = [...adGroup.ads]
                ads[adIndex] = next
                onChange({ ...adGroup, ads })
              }}
            />
          ))}

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <Cap>Keywords · {adGroup.keywords.length}</Cap>
              <div className="mt-1.5 max-h-56 overflow-auto rounded-md border border-ink-750">
                <table className="w-full border-collapse text-left">
                  <thead className="sticky top-0 bg-ink-900">
                    <tr>
                      {['Keyword', 'Match', 'Volume', 'Source'].map((heading) => (
                        <th key={heading} scope="col" className="label-cap px-2.5 py-1.5">
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {adGroup.keywords.map((keyword, index) => (
                      <tr key={`${keyword.text}-${index}`} className="border-t border-ink-800">
                        <td className="num px-2.5 py-1.5 text-[12px] text-mist-200">{keyword.text}</td>
                        <td className="px-2.5 py-1.5">
                          <Badge tone="neutral">{keyword.match_type}</Badge>
                        </td>
                        <td className="num px-2.5 py-1.5 text-[12px] text-mist-400">
                          {keyword.avg_monthly_searches === null
                            ? '—'
                            : formatNumber(keyword.avg_monthly_searches)}
                        </td>
                        <td className="px-2.5 py-1.5 text-[11.5px] text-mist-500">{keyword.source}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <div>
                <Cap>Negatives · {(adGroup.negatives ?? []).length}</Cap>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {(adGroup.negatives ?? []).map((keyword, index) => (
                    <span
                      key={`${keyword.text}-${index}`}
                      className="num rounded border border-ink-700 bg-ink-850 px-1.5 py-[2px] text-[11.5px] text-mist-400"
                      title={`${keyword.match_type} · ${keyword.source}`}
                    >
                      −{keyword.text}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <Cap>Landing page</Cap>
                <div className="mt-1.5 rounded-md border border-ink-750 bg-ink-850/60 px-2.5 py-2">
                  <p className="text-[12.5px] text-mist-100">{adGroup.landing_page.headline}</p>
                  <p className="mt-0.5 text-[11.5px] text-mist-400">{adGroup.landing_page.subheadline}</p>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <Badge tone={adGroup.landing_page.build_status === 'built' ? 'ok' : 'neutral'}>
                      {adGroup.landing_page.build_status}
                    </Badge>
                    <Badge tone="neutral">{adGroup.landing_page.template}</Badge>
                    <code className="num truncate text-[11px] text-mist-500">
                      {adGroup.landing_page.deployed_url ?? adGroup.landing_page.slug}
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function StructurePanel({
  blueprintId,
  highlightPath,
  onSaved,
}: {
  blueprintId: string
  highlightPath?: string | null
  onSaved: (summary: { id: string; version: number }) => void
}) {
  const blueprint = useBlueprint(blueprintId)
  const edit = useEditBlueprint(blueprintId)
  const [draft, setDraft] = useState<Blueprint | null>(null)
  const [dirty, setDirty] = useState(false)
  const [note, setNote] = useState('')

  // Load the document once per version. A background refetch of the *same* version must
  // not clobber unsaved edits, so the reset is keyed on the version number, not on the
  // object identity React Query happens to hand back.
  const loadedVersion = useRef<number | null>(null)
  useEffect(() => {
    if (!blueprint.data) return
    if (loadedVersion.current === blueprint.data.version) return
    loadedVersion.current = blueprint.data.version
    setDraft(structuredClone(blueprint.data))
    setDirty(false)
  }, [blueprint.data])

  const problems = useMemo(() => {
    if (!draft) return []
    const issues: string[] = []
    draft.campaigns.forEach((campaign, campaignIndex) => {
      campaign.ad_groups.forEach((adGroup, adGroupIndex) => {
        adGroup.ads.forEach((ad, adIndex) => {
          ad.headlines.forEach((asset, assetIndex) => {
            const error = headlineError(asset.text)
            if (error) {
              issues.push(`campaigns[${campaignIndex}].ad_groups[${adGroupIndex}].ads[${adIndex}].headlines[${assetIndex}].text: ${error}`)
            }
          })
          ad.descriptions.forEach((asset, assetIndex) => {
            const error = descriptionError(asset.text)
            if (error) {
              issues.push(`campaigns[${campaignIndex}].ad_groups[${adGroupIndex}].ads[${adIndex}].descriptions[${assetIndex}].text: ${error}`)
            }
          })
          if (ad.path1 !== null && ad.path1 !== undefined && ad.path1 !== '') {
            const error = pathError(ad.path1)
            if (error) issues.push(`campaigns[${campaignIndex}].ad_groups[${adGroupIndex}].ads[${adIndex}].path1: ${error}`)
          }
          if (ad.path2 !== null && ad.path2 !== undefined && ad.path2 !== '') {
            const error = pathError(ad.path2)
            if (error) issues.push(`campaigns[${campaignIndex}].ad_groups[${adGroupIndex}].ads[${adIndex}].path2: ${error}`)
          }
        })
      })
    })
    return issues
  }, [draft])

  if (blueprint.isLoading) return <Spinner label="Loading Blueprint…" />
  if (blueprint.isError) return <ErrorNotice error={blueprint.error} />
  if (!draft || !blueprint.data) return null

  // The screen renders the *draft*, not the server copy: an edit must be visible
  // immediately, and saving is what makes it a version.
  const document = draft

  return (
    <div className="flex flex-col gap-3" data-testid="structure-panel">
      <div className="flex flex-wrap items-center gap-2.5">
        <Badge tone="neutral">version {document.version}</Badge>
        <Badge tone="neutral">{document.campaigns.length} campaign(s)</Badge>
        <Badge tone="neutral">
          {document.campaigns.reduce((sum, campaign) => sum + campaign.ad_groups.length, 0)} ad groups
        </Badge>
        <span className="num text-[11.5px] text-mist-500">{document.meta.generator_version}</span>
        {document.meta.policy_lint ? (
          <Badge tone={document.meta.policy_lint.passed ? 'ok' : 'danger'} title={`ruleset ${document.meta.policy_lint.ruleset_version}`}>
            policy linter {document.meta.policy_lint.passed ? 'passed' : 'found violations'}
          </Badge>
        ) : null}
      </div>

      <div className="rounded-lg border border-ink-750 bg-ink-850/40 px-3.5 py-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <div>
            <Cap>Created from</Cap>
            <p className="num text-[12px] text-mist-300">{document.created_from.site_url}</p>
          </div>
          <div>
            <Cap>Crawl snapshot</Cap>
            <p className="num text-[12px] text-mist-400">
              {document.created_from.crawl_snapshot_hash?.slice(0, 18) ?? '—'}
            </p>
          </div>
          <div>
            <Cap>Models</Cap>
            <p className="text-[12px] text-mist-300">
              {Object.entries(document.meta.model_ids ?? {}).map(([role, model]) => (
                <span key={role} className="mr-2">
                  <span className="text-mist-500">{role}:</span> <span className="num">{model}</span>
                </span>
              ))}
            </p>
          </div>
          <div className="ml-auto">
            <Cap>Evidence trail</Cap>
            <p className="text-[12px] text-mist-400">
              prompt hashes {Object.keys(document.meta.prompt_hashes ?? {}).length} · generated{' '}
              {document.meta.generated_at}
            </p>
          </div>
        </div>
      </div>

      {document.campaigns.map((campaign, campaignIndex) => (
        <div key={`${campaign.name}-${campaignIndex}`} className="flex flex-col gap-2.5">
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-ink-750 bg-ink-900 px-3.5 py-2.5">
            <div>
              <Cap>Campaign</Cap>
              <p className="text-[13.5px] font-medium text-mist-100">{campaign.name}</p>
            </div>
            <div className="num text-[12px] text-mist-300">
              {formatMoneyMicros(campaign.daily_budget_micros)}/day
            </div>
            <Badge tone="forge">{campaign.bid_strategy.kind}</Badge>
            <Badge tone="neutral" title={campaign.bid_strategy.rationale}>
              template {campaign.bid_strategy.template_id} v{campaign.bid_strategy.template_version}
            </Badge>
            <div className="num ml-auto text-[11.5px] text-mist-400">
              {campaign.geo_targets.map((geo) => geo.name).join(' · ')} ·{' '}
              {campaign.language_targets.join(',')}
            </div>
          </div>

          {campaign.ad_groups.map((adGroup, adGroupIndex) => (
            <AdGroupBlock
              key={`${adGroup.name}-${adGroupIndex}`}
              campaignIndex={campaignIndex}
              adGroup={adGroup}
              adGroupIndex={adGroupIndex}
              highlightPath={highlightPath ?? null}
              onChange={(next) => {
                setDirty(true)
                setDraft((prev) => {
                  if (!prev) return prev
                  const campaigns = [...prev.campaigns]
                  const target = campaigns[campaignIndex]!
                  const adGroups = [...target.ad_groups]
                  adGroups[adGroupIndex] = next
                  campaigns[campaignIndex] = { ...target, ad_groups: adGroups }
                  return { ...prev, campaigns }
                })
              }}
            />
          ))}
        </div>
      ))}

      {(document.shared_negatives ?? []).length > 0 ? (
        <Panel title="Account-level negatives" subtitle="Applied across the account, not just one ad group.">
          {(document.shared_negatives ?? []).map((list) => (
            <div key={list.name} className="mb-2 last:mb-0">
              <Cap>
                {list.name} · {list.scope}
              </Cap>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {list.keywords.map((keyword, index) => (
                  <span
                    key={`${keyword.text}-${index}`}
                    className="num rounded border border-ink-700 bg-ink-850 px-1.5 py-[2px] text-[11.5px] text-mist-400"
                  >
                    −{keyword.text}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </Panel>
      ) : null}

      <div className="sticky bottom-3 z-20">
        <div className="panel-raised flex flex-wrap items-center gap-3 px-3.5 py-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-medium text-mist-100">
                {dirty ? 'Unsaved edits' : 'No unsaved edits'}
              </span>
              {problems.length > 0 ? (
                <Badge tone="danger">{problems.length} invalid</Badge>
              ) : dirty ? (
                <Badge tone="ok">all assets inside limits</Badge>
              ) : null}
            </div>
            <p className="mt-0.5 text-[11.5px] text-mist-500">
              Saving creates version {document.version + 1}. Versions are immutable, so the diff and the
              audit trail survive.
            </p>
          </div>
          <label className="flex items-center gap-2">
            <span className="label-cap">Note</span>
            <Input
              aria-label="Edit note"
              data-testid="edit-note"
              value={note}
              placeholder="why this edit?"
              onChange={(event) => setNote(event.target.value)}
              className="w-56"
            />
          </label>
          <Button
            variant="ghost"
            disabled={!dirty}
            onClick={() => {
              setDraft(structuredClone(document))
              setDirty(false)
            }}
          >
            Discard
          </Button>
          <Button
            variant="primary"
            data-testid="save-version"
            disabled={!dirty || problems.length > 0 || edit.isPending}
            onClick={async () => {
              const summary = await edit.mutateAsync({
                document: draft,
                note: note.trim() === '' ? null : note.trim(),
              })
              setNote('')
              onSaved({ id: summary.id, version: summary.version })
            }}
          >
            {edit.isPending ? 'Saving…' : `Save as v${document.version + 1}`}
          </Button>
        </div>
        {problems.length > 0 ? (
          <div className="mt-2 rounded-lg border border-signal-danger/45 bg-signal-danger/10 px-3.5 py-2.5">
            <Cap>Refused — the frozen schema rejects these</Cap>
            <ul className="mt-1 flex flex-col gap-0.5">
              {problems.slice(0, 6).map((problem) => (
                <li key={problem} className="num text-[11.5px] text-mist-200">
                  {problem}
                </li>
              ))}
            </ul>
            {problems.length > 6 ? (
              <p className="mt-1 text-[11.5px] text-mist-400">+{problems.length - 6} more</p>
            ) : null}
          </div>
        ) : null}
        {edit.isError ? <div className="mt-2"><ErrorNotice error={edit.error} /></div> : null}
      </div>

      {document.campaigns.length === 0 ? (
        <EmptyState title="This Blueprint has no campaigns" body="Generate a structure first." />
      ) : null}
    </div>
  )
}
